import React, { memo } from 'react'

import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import { alpha, IconButton, Typography, useTheme } from '@mui/material'

import Icon from 'src/@core/components/icon'
import FallbackSpinner from 'src/@core/components/spinner'

type InferValueType<T> = T extends { value: infer V } ? V : T

export interface CustomSelectOption {
  value: string | number | undefined
  name: string
  additionalValue?: JSX.Element
  highlight?: boolean
  disabled?: boolean
}

export type CustomSelectValue = string | number | undefined | CustomSelectOption

export type CustomSelectProps<T extends CustomSelectValue> = {
  loading?: boolean
  labelText?: string
  value: InferValueType<T>
  handleValueChange: (event: SelectChangeEvent<InferValueType<T>>) => void
  selectItems: T[]
  defaultValue?: InferValueType<T>
  fullWidth?: boolean
  error?: boolean
  helperText?: string
  menuMaxHeight?: number
  hideAdditionalValueInOptions?: boolean
} & SelectProps

const StyledSelect = styled(Select)<SelectProps>(({ theme }) => ({
  height: 'max-content',
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: 'none !important',
  '&.MuiOutlinedInput-root': {
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: theme.spacing(1.5)
  },
  '& .MuiSelect-select.MuiInputBase-input': {
    padding: theme.spacing(1.875, 3.25, 1.875, 3.25)
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 0
  }
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
  lineHeight: 1.154,
  margin: theme.spacing(1, 0, 0),
  fontSize: theme.typography.body2.fontSize
}))

const CustomSelect = <T extends CustomSelectValue>({
  loading,
  labelText,
  value,
  handleValueChange,
  selectItems,
  defaultValue,
  placeholder,
  error,
  helperText,
  fullWidth = false,
  disabled = false,
  hideAdditionalValueInOptions = true,
  required,
  menuMaxHeight,
  ...rest
}: CustomSelectProps<T>) => {
  const theme = useTheme()

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    handleValueChange(event as SelectChangeEvent<InferValueType<T>>)
  }

  const [open, setOpen] = React.useState(false)

  const handleToggleOpen = (event: React.SyntheticEvent<Element, Event>) => {
    const hasClickedOnAdditionalValue = event.target && (event?.target as HTMLElement).closest('.additional-value')

    if (hasClickedOnAdditionalValue) {
      event.stopPropagation() // Prevent the dropdown from toggling

      return
    }

    if (!disabled) setOpen(prev => !prev)
  }

  return (
    <Stack width={fullWidth ? '100%' : 'auto'}>
      {labelText && (
        <Typography
          required={required}
          marginBottom={1}
          lineHeight={1.1}
          color={theme.palette.grey[900]}
          component={InputLabel}
          variant='body2'
        >
          {labelText}
        </Typography>
      )}

      <StyledSelect
        labelId='custom-select-label'
        variant='outlined'
        open={open}
        onOpen={handleToggleOpen}
        onClose={handleToggleOpen}
        value={value}
        error={error}
        disabled={disabled}
        onChange={handleChange}
        defaultValue={defaultValue}
        required={required}
        IconComponent={() => (
          <IconButton onClick={handleToggleOpen}>
            <Icon color={theme.palette.grey[400]} icon='tabler:chevron-down' />
          </IconButton>
        )}
        displayEmpty={!!placeholder}
        {...(menuMaxHeight && { MenuProps: { PaperProps: { style: { maxHeight: menuMaxHeight } } } })}
        {...(placeholder && {
          renderValue: selectedValue => {
            if (!selectedValue || selectedValue === '') {
              return <Typography color={alpha('#4B465C14', 0.2)}>{placeholder}</Typography>
            }

            const selectedItem = selectItems.find(item => {
              if (typeof item === 'string' || typeof item === 'number') {
                return item === selectedValue
              }

              return item?.value === selectedValue
            })

            if (selectedItem && typeof selectedItem !== 'string' && typeof selectedItem !== 'number') {
              return (
                <Stack gap={theme.spacing(2)} direction='row'>
                  {selectedItem.name}

                  {selectedItem.additionalValue && (
                    <Stack
                      className='additional-value'
                      onClick={e => e.stopPropagation()} // Prevent click propagation
                    >
                      {selectedItem.additionalValue}
                    </Stack>
                  )}
                </Stack>
              )
            }

            return selectedItem ?? placeholder
          }
        })}
        {...rest}
      >
        {loading && <FallbackSpinner fullHeight={false} />}

        {selectItems.map(item => {
          if (typeof item === 'string' || typeof item === 'number') {
            return (
              <MenuItem key={item} value={item} disabled={disabled}>
                {item}
              </MenuItem>
            )
          }

          if (item?.value === null) return null

          return (
            <MenuItem
              key={item?.value}
              value={item?.value}
              disabled={item?.disabled}
              sx={{ color: item?.highlight ? theme.palette.primary[500] : theme.palette.secondary[900] }}
            >
              <Stack gap={theme.spacing(2)} direction='row'>
                {item?.name}
                {!hideAdditionalValueInOptions && item?.additionalValue && item?.additionalValue}
              </Stack>
            </MenuItem>
          )
        })}
      </StyledSelect>

      {helperText && (
        <StyledTypography color={error ? theme.palette.error.main : theme.palette.text.secondary}>
          {helperText}
        </StyledTypography>
      )}
    </Stack>
  )
}

export default memo(CustomSelect)
